import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader, LoadingState } from '@v2/design-system';
import TablePagination, { TABLE_PER_PAGE_OPTIONS } from '@v2/components/TablePagination';
import api from '@v2/lib/api';
import type { AuditLog } from './types';

const ACTION_BADGE: Record<string, string> = {
    created:                'bg-green-100 text-green-700',
    updated:                'bg-blue-100 text-blue-700',
    deleted:                'bg-red-100 text-red-700',
    nf_submitted:           'bg-yellow-100 text-yellow-700',
    paid:                   'bg-green-100 text-green-700',
    cancelled:              'bg-gray-100 text-gray-600',
    commission_calculated:  'bg-orange-100 text-orange-700',
    nf_manual_reminder:     'bg-amber-100 text-amber-800',
    nf_auto_reminder:       'bg-amber-100 text-amber-800',
    nf_received_email:      'bg-sky-100 text-sky-700',
    nf_rescheduled_email:   'bg-purple-100 text-purple-700',
    nf_period_rolled:       'bg-purple-100 text-purple-700',
    payment_batch_approved: 'bg-emerald-100 text-emerald-700',
};

function ChangeCell({ log }: { log: AuditLog }) {
    if (!log.changes.length) {
        return <span className="text-gray-400">—</span>;
    }

    return (
        <details>
            <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                Ver alterações ({log.changes.length})
            </summary>
            <div className="mt-1 space-y-1">
                {log.changes.map((change) => (
                    <div key={change.field}>
                        <span className="font-medium text-gray-600">{change.field_label}:</span>{' '}
                        {change.old != null ? (
                            <>
                                <span className="text-red-600 line-through">{change.old}</span>
                                {' → '}
                                <span className="text-green-600">{change.new ?? '—'}</span>
                            </>
                        ) : (
                            <span className="text-green-600">{change.new ?? '—'}</span>
                        )}
                    </div>
                ))}
            </div>
        </details>
    );
}

export default function CommissionAuditPage() {
    const [filters, setFilters] = useState({ entity_type: '', action: '', from: '', to: '', page: 1, per_page: 25 });

    const { data, isLoading } = useQuery({
        queryKey: ['commissioning', 'audit', filters],
        queryFn: () => api.get('/commissioning/audit-logs', { params: filters }).then((r) => r.data),
        placeholderData: (prev) => prev,
    });

    const logs: AuditLog[] = data?.data ?? [];
    const meta = data?.meta;
    const entityOptions = meta?.filter_entities ?? [];
    const actionOptions = meta?.filter_actions ?? [];

    return (
        <div className="space-y-6">
            <PageHeader title="Log de Auditoria" subtitle="Histórico completo de alterações nas regras e registros de comissão" />

            <div className="flex flex-wrap gap-3">
                <select
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    value={filters.entity_type}
                    onChange={(e) => setFilters((p) => ({ ...p, entity_type: e.target.value, page: 1 }))}
                >
                    <option value="">Todos os tipos</option>
                    {entityOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
                <select
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    value={filters.action}
                    onChange={(e) => setFilters((p) => ({ ...p, action: e.target.value, page: 1 }))}
                >
                    <option value="">Todas as ações</option>
                    {actionOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
                <input
                    type="date"
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    value={filters.from}
                    onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value, page: 1 }))}
                />
                <input
                    type="date"
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    value={filters.to}
                    onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value, page: 1 }))}
                />
                {meta && <span className="text-sm text-gray-500 self-center ml-auto">{meta.total} registros</span>}
            </div>

            {isLoading && <LoadingState variant="table" />}

            <div className="rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                        <tr>
                            <th className="px-4 py-3 text-left font-medium">Data</th>
                            <th className="px-4 py-3 text-left font-medium">Usuário</th>
                            <th className="px-4 py-3 text-left font-medium">Entidade</th>
                            <th className="px-4 py-3 text-left font-medium">Ação</th>
                            <th className="px-4 py-3 text-left font-medium">Descrição</th>
                            <th className="px-4 py-3 text-left font-medium">Alterações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {logs.map((l) => (
                            <tr key={l.id} className="hover:bg-gray-50 align-top">
                                <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{l.created_at}</td>
                                <td className="px-4 py-3 font-medium">{l.changed_by}</td>
                                <td className="px-4 py-3 text-gray-600 text-xs">
                                    {l.entity_type_label}{' '}
                                    <span className="text-gray-400">#{l.entity_id}</span>
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ACTION_BADGE[l.action] ?? 'bg-gray-100 text-gray-600'}`}>
                                        {l.action_label}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-gray-600 max-w-[220px]">{l.description ?? '—'}</td>
                                <td className="px-4 py-3 text-xs max-w-[280px]">
                                    <ChangeCell log={l} />
                                </td>
                            </tr>
                        ))}
                        {logs.length === 0 && !isLoading && (
                            <tr>
                                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">Nenhum registro encontrado.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {meta && (
                <TablePagination
                    meta={meta}
                    onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
                    onPerPageChange={(per_page) => setFilters((f) => ({ ...f, per_page, page: 1 }))}
                    perPageOptions={[...TABLE_PER_PAGE_OPTIONS]}
                    itemLabel="registro"
                />
            )}
        </div>
    );
}
